import axios from "axios";
import { toast } from "react-toastify";

let isRefreshing = false;
let refreshSubscribers = [];
let isErrorDisplay = false;

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

export const refreshAccessToken = async () => {
  try {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const response = await fetch(`${apiBaseUrl}auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        user_agent_type: 'ADMIN',
        refresh_token: localStorage.getItem('refresh_token')
      }),
    });
    if (!response.ok) {
      throw new Error("Failed to refresh access token");
    }

    const data = await response.json();
    const { access_token, refresh_token } = data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("refresh_token", refresh_token);
    return access_token;
  } catch (error) {
    // Extra safety in case error bypasses the check above
    if (!isErrorDisplay) {
      isErrorDisplay = true;
      toast.error("Your session has been expired. Please login again.");
    }
    localStorage.clear();
    setTimeout(() => {
      isErrorDisplay = false;
      window.location.href = "/login";
    }, 1500);
    throw error;
  }
};

// Create instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL, // Replace with actual API URL
  headers: {
    // "Content-Type": "application/json",
    "user-agent-type": 'ADMIN',
  },
});

// Request Interceptor
api.interceptors.request.use((config) => {
  const skipAuth = config?.headers?.skipAuth;

  if (!skipAuth) {
    const token =
      localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } else {
    // If skipAuth is true, remove Authorization header
    delete config.headers.Authorization;
  }

  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  } else {
    config.headers["Content-Type"] = "application/json";
  }

  delete config.headers.skipAuth; // remove custom header after use
  return config;
});

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If already retried once, don’t loop infinitely
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // 🔑 Only refresh on 403
    if (error.response?.status === 403) {
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;
        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          onRefreshed(newToken);

          // retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          localStorage.clear();
          setTimeout(() => {
            window.location.href = "/login";
          }, 1500);
          return Promise.reject(refreshError);
        }
      }

      // If refresh already in progress, queue this request
      return new Promise((resolve) => {
        subscribeTokenRefresh((newToken) => {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(originalRequest));
        });
      });
    }

    // 🚫 401 unauthorize
    if (error.response?.status === 401) {
      if (!isErrorDisplay) {
        isErrorDisplay = true;
        toast.error("You do not have permission to access this resource.");
      }
      localStorage.clear();
      setTimeout(() => {
        isErrorDisplay = false;
        if (window.location.pathname != "/login")
          window.location.href = "/login";
      }, 1500);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);


export default api;


/**
 * Helper function to send FormData requests
 * Automatically handles multipart headers
 */
export const apiFormData = async ({
  method = "POST",
  url,
  data,
  params = {},
  skipAuth = false,
}) => {
  if (!(data instanceof FormData)) {
    throw new Error("apiFormData requires FormData instance");
  }

  const response = await api({
    method,
    url,
    data,
    params,
    headers: {
      ...(skipAuth && { skipAuth: true }),
    },
  });

  return response.data;
};

